import { useCallback, useEffect, useRef, useState } from 'react';
import { Close, Notification } from '@sas/react-svg';
import cx from 'classnames';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { useResizeObserverRef } from '../../hooks/useResizeObserver';
import { useIsRTL } from '../../locale';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { renderDesignTokens, useDesignTokens } from '../../theme';
import { Button, ButtonVariant } from '../Button';
import { BaseButton } from '../Button/__internal__';
import { Icon } from '../Icon';
import { Flow } from '../Layouts/Flow';
import { Text } from '../Text';
import bundle from './NovaComponents-MessageToast-gui-icu.properties';
import cn from './MessageToast.css.json';
import './MessageToast.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
export const MessageToast = ({
  displayMode = 'primary',
  referenceElementDock = ['start', 'end'],
  toastDock = ['start', 'end'],
  offset = [0, 0],
  collision = 'fit',
  tokens: tokenOverrides,
  theme,
  ...props
}) => {
  const isRTL = useIsRTL();
  const dt = useDesignTokens();
  const localize = useTextLocalizer();
  const ref = useRef(null);
  const [left, setLeft] = useState();
  const [top, setTop] = useState();
  const useHtmlId = createUseHtmlId('message-toast');
  const contentId = useHtmlId();
  const [{
    elementAcceptsAnimation,
    prevIsOpen,
    closing
  }, setAnimation] = useState({
    elementAcceptsAnimation: false,
    prevIsOpen: props.isOpen,
    closing: false
  });
  if (props.isOpen !== prevIsOpen) {
    setAnimation(prev => ({
      ...prev,
      prevIsOpen: props.isOpen,
      closing: !props.hideAnimation && prev.elementAcceptsAnimation && prevIsOpen
    }));
  }
  const tokens = {
    ...getMessageToastDesignTokens(dt, {
      displayMode,
      tokens: tokenOverrides
    }),
    ...theme?.(dt)
  };
  const updatePosition = useCallback(() => {
    if (!ref.current) return;
    const refElement = props.referenceElementRef?.current ?? document.body;
    const [left, top] = calculateLeftTop(ref.current, toastDock, refElement, referenceElementDock, offset, collision, isRTL);
    setLeft(left);
    setTop(top);
  }, [collision, isRTL, offset, props.referenceElementRef, referenceElementDock, toastDock]);
  useEffect(updatePosition, [updatePosition]);
  const [setToastResizeRef] = useResizeObserverRef(() => {
    // This will be called once suspense has finished
    if (left === undefined || top === undefined) {
      // Only update position if the left/top is uninitialized
      updatePosition();
    }
  });
  const hasOffset = offset[0] !== 0 || offset[1] !== 0;
  const referenceElementDockPosition = `${referenceElementDock[0]}${referenceElementDock[1].charAt(0).toUpperCase()}${referenceElementDock[1].slice(1)}`;
  const openingAnimClass = props.referenceElementRef || hasOffset ? cn.entering.centerCenter : isRTL ? cn.novaMessagetoastSlideinRtl : cn.entering[referenceElementDockPosition];
  const closingAnimClass = elementAcceptsAnimation ? props.referenceElementRef || hasOffset ? cn.closing.centerCenter : isRTL ? cn.novaMessagetoastSlideoutRtl : cn.closing[referenceElementDockPosition] : false;

  // line-height patch specific to message toast to help keep message toast's icon and its text centered
  // NOTE: this will break if the unit is not px
  const textFontSizeInPixels = parseFloat(dt.enableNovaTokens ? dt.tokens['font-size-body-text-m'] : dt.reportTokens.font.size.m) * 16;
  const textHeight = Math.round(textFontSizeInPixels * (dt.enableNovaTokens ? parseFloat(dt.tokens['font-space-line-height']) : dt.reportTokens.font.lineHeight));

  // Determine how many lines of text will fit in a toast at current screen size
  // Allow Message Toast to use up to 90% of the inner screen height
  // We account for 1rem of padding at the top and bottom by subtracting 16px for each rem
  const maxLines = Math.trunc((window.innerHeight * 0.9 - 32) / textHeight);

  // the 14 is the height of the message strip's icon in pixels.
  const marginTopCalc = `${Math.round((textHeight - 14) / 2)}px`;
  const animationEndHandler = useCallback(event => {
    if (event.target === event.currentTarget) {
      if (closing) {
        setAnimation(prev => ({
          ...prev,
          prevIsOpen: false,
          closing: false
        }));
      } else {
        setAnimation({
          elementAcceptsAnimation: true,
          prevIsOpen: false,
          closing: false
        });
      }
    }
  }, [closing]);
  useEffect(() => {
    const toastElement = ref.current;
    if (toastElement) {
      toastElement.addEventListener('animationend', animationEndHandler);
      return () => {
        toastElement.removeEventListener('animationend', animationEndHandler);
      };
    }
  }, [animationEndHandler]);
  return props.isOpen || closing ? /*#__PURE__*/_jsx("div", {
    ref: element => {
      ref.current = element;
      setToastResizeRef(element);
    },
    "data-testid": props.testId,
    className: cx(cn.container, props.className),
    style: {
      ...tokens,
      visibility: left !== undefined && top !== undefined ? undefined : 'hidden',
      left,
      top,
      ...props.style
    },
    children: /*#__PURE__*/_jsxs("div", {
      className: cx(cn.containerInner, !props.hideAnimation && openingAnimClass, closing && closingAnimClass),
      ref: props.toastRef,
      "data-closing-anim": closingAnimClass,
      onMouseEnter: props.onMouseEnter,
      onMouseLeave: props.onMouseLeave,
      onKeyDown: e => {
        if (e.key === 'Escape' && props.onClose) {
          props.onClose();
        }
      },
      tabIndex: -1,
      children: [/*#__PURE__*/_jsx("div", {
        className: cx(cn.containerInnerBorder, !props.hideAnimation && cn.enteringBorderAnim, closing && cn.closingBorderAnim)
      }), props.hideIcon ? null : /*#__PURE__*/_jsx(Icon, {
        className: cn.toastIcon,
        icon: Notification,
        theme: () => {
          return {
            '--icon-default-override-fg': 'var(--toast-fg)'
          };
        },
        style: {
          marginTop: marginTopCalc
        }
      }), /*#__PURE__*/_jsxs(Flow, {
        orientation: "vertical",
        className: cn.toastContent,
        children: [props.children ? /*#__PURE__*/_jsx(Text, {
          id: contentId,
          className: cn.sectionMessage,
          style: {
            WebkitLineClamp: maxLines
          },
          theme: () => {
            return {
              '--text-fg': 'var(--toast-fg)'
            };
          },
          children: props.children
        }) : null, props.actionButton ? /*#__PURE__*/_jsx(BaseButton, {
          ref: props.actionButton?.ref,
          variant: ButtonVariant.LightSmall,
          className: cx(cn.actionButton, cn.focusIndicator),
          theme: dt => {
            if (dt.baseReportThemeName === 'highcontrast2018' && displayMode === 'secondary') {
              return {
                '--button-fg': 'var(--toast-fg)',
                '--button-filter': 'none',
                '--button-bg': 'transparent',
                '--button-active-fg': 'var(--toast-fg)',
                '--button-active-bg': 'transparent',
                '--button-active-filter': 'none',
                '--button-hover-fg': 'var(--toast-fg)',
                '--button-hover-bg': 'transparent',
                '--button-hover-filter': 'none'
              };
            }
            if (dt.themeVariant === 'high-contrast' && displayMode === 'secondary') {
              return {
                '--button-active-filter': 'none'
              };
            }
          },
          onPress: () => {
            props.actionButton?.onPress?.();
            props.onClose?.();
          },
          text: localize(`action.button.${props.actionButton.type}.label`),
          ariaDescribedBy: contentId,
          onBlur: props.actionButton.onBlur
        }) : null]
      }), props.closeable ? /*#__PURE__*/_jsx(Button, {
        ref: props.closeButtonRef,
        className: cx(cn.closeButton, cn.focusIndicator),
        style: isRTL ? {
          left: 0,
          right: 'auto'
        } : {},
        variant: ButtonVariant.Icon,
        onPress: props.onClose,
        focusable: true,
        theme: dt => {
          if (dt.enableNovaTokens) return {};
          if (dt.baseReportThemeName === 'highcontrast2018' && displayMode === 'secondary') {
            return {
              '--button-fg': 'var(--toast-fg)',
              '--button-filter': 'none',
              '--button-bg': 'transparent',
              '--button-hover-fg': 'var(--toast-fg)',
              '--button-hover-filter': 'none',
              '--button-hover-bg': 'transparent',
              '--button-active-fg': 'var(--toast-fg)',
              '--button-active-filter': 'none',
              '--button-active-bg': 'transparent'
            };
          }
          if (dt.themeVariant === 'high-contrast') {
            return {
              '--button-active-fg': dt.reportTokens.stateStandard.active.fg,
              '--button-active-filter': displayMode === 'primary' ? 'invert(1) grayscale(1)' : 'none'
            };
          }
          return {
            '--button-fg': 'var(--toast-fg)',
            '--button-hover-fg': 'var(--toast-fg)',
            '--button-active-fg': 'var(--toast-fg)'
          };
        },
        icon: {
          icon: Close,
          tooltip: localize('close.icon.tooltip.label')
        }
      }) : null]
    })
  }) : null;
};
export const getMessageToastDesignTokens = (dt, opts) => {
  return {
    ...renderDesignTokens({
      'spacing-m': dt.tokens['space-base-m'],
      'spacing-xl': dt.tokens['space-base-xl'],
      'spacing-s': dt.tokens['space-base-s'],
      'spacing-none': dt.tokens['space-base-null'],
      'accent-bd-width': dt.tokens['size-border-width-l'],
      'accent-bd-color': dt.tokens['color-action-border-emphasis-normal'],
      'font-size-m': dt.tokens['font-size-body-text-m'],
      'anim-dur': dt.tokens['duration-300'],
      'anim-tf-enter-exit': dt.tokens['ease-in-out'],
      'anim-tf-exit': dt.tokens['ease-out'],
      'action-bg': dt.tokens['color-container-background-2'],
      'block-margin': dt.tokens['space-base-s'],
      fg: dt.tokens['color-text-primary'],
      bg: dt.tokens['color-container-background-2'],
      bd: dt.themeVariant === 'high-contrast' ? dt.tokens['color-container-border-4'] : 'none',
      'border-width': dt.tokens['size-border-width-s'],
      sh: dt.tokens['shadow-depth-s'],
      focus: 'var(--focus-light-bg-color)',
      ...opts?.tokens?.()
    }, 'toast'),
    ...(!dt.enableNovaTokens && getMessageToastLegacyDesignTokens(dt, opts))
  };
};
export const getMessageToastLegacyDesignTokens = (dt, opts) => {
  const toastTokens = {
    'accent-bd-width': '1px',
    'accent-bd-color': dt.reportTokens.backgroundsDepth.large.bd,
    'font-size-m': dt.reportTokens.font.size.m,
    'action-bg': dt.reportTokens.backgrounds.page.bg
  };
  switch (dt.baseReportThemeName) {
    case 'highcontrast2018':
    case 'highcontrast2017':
      return renderDesignTokens(opts?.displayMode === 'primary' ? {
        fg: dt.c.white,
        bg: dt.c.black,
        bd: dt.reportTokens.backgroundsDepth.large.bd,
        sh: dt.reportTokens.backgroundsDepth.large.sh,
        focus: dt.c.hcFocus,
        ...toastTokens
      } : {
        fg: dt.c.black,
        bg: dt.c.secondary,
        bd: dt.reportTokens.backgroundsDepth.large.bd,
        sh: dt.reportTokens.backgroundsDepth.large.sh,
        focus: dt.c.black,
        ...toastTokens
      }, 'toast');
  }
  switch (dt.themeVariant) {
    case 'dark':
      return renderDesignTokens(opts?.displayMode === 'primary' ? {
        fg: dt.c.white,
        bg: dt.c.p_2,
        bd: dt.reportTokens.backgroundsDepth.large.bd,
        sh: dt.reportTokens.backgroundsDepth.large.sh,
        focus: dt.c.p1,
        ...toastTokens
      } : {
        fg: dt.c.white,
        bg: dt.c.secondary,
        bd: dt.reportTokens.backgroundsDepth.large.bd,
        sh: dt.reportTokens.backgroundsDepth.large.sh,
        focus: dt.tokens['color-body-text'],
        ...toastTokens
      }, 'toast');
    case 'high-contrast':
      return renderDesignTokens(opts?.displayMode === 'primary' ? {
        fg: dt.c.white,
        bg: dt.c.black,
        bd: dt.reportTokens.backgroundsDepth.large.bd,
        sh: dt.reportTokens.backgroundsDepth.large.sh,
        focus: dt.c.w1_5,
        ...toastTokens
      } : {
        fg: dt.c.black,
        bg: dt.c.e2,
        bd: dt.reportTokens.backgroundsDepth.large.bd,
        sh: dt.reportTokens.backgroundsDepth.large.sh,
        focus: dt.c.black,
        ...toastTokens
      }, 'toast');
    default:
      return renderDesignTokens(opts?.displayMode === 'primary' ? {
        fg: dt.c.black,
        bg: dt.c.white,
        bd: dt.reportTokens.backgroundsDepth.large.bd,
        sh: dt.reportTokens.backgroundsDepth.large.sh,
        focus: dt.c.p_1,
        ...toastTokens
      } : {
        fg: dt.c.white,
        bg: dt.c.secondary,
        bd: dt.reportTokens.backgroundsDepth.large.bd,
        sh: dt.reportTokens.backgroundsDepth.large.sh,
        focus: dt.c.white,
        ...toastTokens
      }, 'toast');
  }
};
const getFlippedPosition = dockPositionType => {
  switch (dockPositionType) {
    case 'start':
      return 'end';
    case 'center':
      return 'center';
    case 'end':
      return 'start';
  }
};
const getDockPoint = params => {
  const {
    width,
    height,
    left,
    top,
    dockPosition: [dockPositionX, dockPositionY],
    isRTL
  } = params;
  const getLeft = dockPositionX => {
    switch (dockPositionX) {
      case 'start':
        return isRTL ? left + width : left;
      case 'center':
        return left + width / 2;
      case 'end':
        return isRTL ? left : left + width;
    }
  };
  const getTop = dockPositionY => {
    switch (dockPositionY) {
      case 'start':
        return top;
      case 'center':
        return top + height / 2;
      case 'end':
        return top + height;
    }
  };
  return {
    left: getLeft(dockPositionX),
    top: getTop(dockPositionY),
    flippedLeft: getLeft(getFlippedPosition(dockPositionX)),
    flippedTop: getTop(getFlippedPosition(dockPositionY))
  };
};
const getValueByCollision = (value, flippedValue, minimum, maximum, collisionType) => {
  const getHiddenLength = value => {
    if (value >= minimum && value <= maximum) return 0;
    return value > maximum ? value - maximum : minimum - value;
  };
  switch (collisionType) {
    case 'none':
      return value;
    case 'fit':
      return Math.min(maximum, Math.max(minimum, value));
    case 'flip':
      const hiddenLength = getHiddenLength(value);
      const flippedHiddenLength = getHiddenLength(flippedValue);
      return hiddenLength <= flippedHiddenLength ? value : flippedValue;
  }
};
const calculateLeftTop = (toastElement, toastDock, refElement, refElementDock, offset, collision, isRTL) => {
  const {
    width: toastWidth,
    height: toastHeight
  } = toastElement.getBoundingClientRect();
  if (toastWidth === 0 || toastHeight === 0) return [undefined, undefined];
  const refElementRect = refElement.getBoundingClientRect();
  const {
    left: refElementLeft,
    top: refElementTop,
    flippedLeft: flippedRefElementLeft,
    flippedTop: flippedRefElementTop
  } = getDockPoint({
    left: refElementRect.left,
    // Use left/top rather than x/y because Legacy Edge does not have x/y
    top: refElementRect.top,
    width: refElementRect.width,
    height: refElementRect.height,
    dockPosition: refElementDock,
    isRTL
  });
  const {
    left: toastLeft,
    top: toastTop,
    flippedLeft: flippedToastLeft,
    flippedTop: flippedToastTop
  } = getDockPoint({
    left: 0,
    top: 0,
    width: toastWidth,
    height: toastHeight,
    dockPosition: toastDock,
    isRTL
  });
  const left = refElementLeft - toastLeft + offset[0];
  const top = refElementTop - toastTop + offset[1];
  const flippedLeft = flippedRefElementLeft - flippedToastLeft + offset[0];
  const flippedTop = flippedRefElementTop - flippedToastTop + offset[1];
  const bodyRect = document.body.getBoundingClientRect();
  const maxTop = bodyRect.top + bodyRect.height - toastHeight;
  const minTop = bodyRect.top;
  const maxLeft = bodyRect.left + bodyRect.width - toastWidth;
  const minLeft = bodyRect.left;
  const collisionX = typeof collision === 'string' ? collision : collision[0];
  const collisionY = typeof collision === 'string' ? collision : collision[1];
  const resultLeft = getValueByCollision(left, flippedLeft, minLeft, maxLeft, collisionX);
  const resultTop = getValueByCollision(top, flippedTop, minTop, maxTop, collisionY);
  return [resultLeft, resultTop];
};
//# sourceMappingURL=MessageToast.js.map